import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
import chroma from 'chroma-js';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import CloseIcon from '@material-ui/icons/Close';
import TuneIcon from '@material-ui/icons/Tune';
import GradientIcon from '@material-ui/icons/Gradient';
import { ChromePicker } from 'react-color';
import { getBestTextColor } from './helpers/accessibilityHelpers';
import { useIsMobile } from './hooks/useIsMobile';

const SHADE_COUNT = 17;

// Build a light -> base -> dark ramp that preserves the hue.
function buildShades(hex) {
  try {
    const light = chroma(hex).set('hsl.l', 0.94).hex();
    const dark = chroma(hex).set('hsl.l', 0.12).hex();
    return chroma.scale([light, hex, dark]).mode('lab').colors(SHADE_COUNT);
  } catch (e) {
    return [hex];
  }
}

const useStyles = makeStyles({
  column: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'flex 0.35s ease',
    cursor: 'pointer',
    '&:hover $toolbar': { opacity: 1 },
    '@media (max-width: 900px)': {
      flex: '1 1 50%',
      height: '40vh',
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.1rem',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    '@media (max-width: 900px)': { opacity: 1 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  iconButton: { padding: '8px' },
  info: {
    position: 'absolute',
    bottom: '11vh',
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    '@media (max-width: 900px)': { bottom: '2vh' },
  },
  hex: {
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    userSelect: 'none',
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    '@media (max-width: 600px)': { fontSize: '1rem' },
  },
  name: {
    marginTop: '0.5rem',
    fontSize: '0.7rem',
    letterSpacing: '0.05em',
    textTransform: 'capitalize',
    opacity: 0.6,
    userSelect: 'none',
    '@media (max-width: 600px)': { fontSize: '0.6rem' },
  },
  lockBadge: {
    position: 'absolute',
    top: '1.25rem',
    opacity: 0.9,
  },
  pickerWrap: {
    position: 'absolute',
    bottom: '24vh',
    zIndex: 60,
  },
  // Full-column vertical shade ramp shown when "View shades" is active. The
  // bands are always visible (a continuous light->dark ramp); the hex label
  // appears on the band under the cursor, and a dot marks the current color.
  shades: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 55,
  },
  shadeRow: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    '&:hover $shadeLabel': { opacity: 1 },
  },
  shadeLabel: {
    opacity: 0,
    transition: 'opacity 0.12s ease',
    pointerEvents: 'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  shadeDot: {
    position: 'absolute',
    left: '50%',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    zIndex: 57,
  },
  shadesClose: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    zIndex: 58,
    backgroundColor: 'rgba(255,255,255,0.75)',
    color: '#222',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
  },

  // ---- Mobile fullscreen overlays (shades + color picker) ----
  // A single tappable surface that fills the viewport so individual shades and
  // the color wheel are large enough to use comfortably on a phone.
  fsOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    background: '#111',
  },
  fsHeader: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    color: '#fff',
    background: 'rgba(0,0,0,0.85)',
  },
  fsTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  fsClose: {
    color: '#fff',
  },
  // Vertical stack of full-width shade bands; each band is tall enough to tap.
  fsShades: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  fsShadeRow: {
    flex: 1,
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  fsShadeRowActive: {
    boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.9)',
  },
  // Centered, enlarged color-wheel picker.
  fsPickerBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    '& .chrome-picker': {
      boxShadow: '0 10px 40px rgba(0,0,0,0.5) !important',
      borderRadius: '10px !important',
    },
  },
});

/**
 * A single full-height generator swatch styled after Coolors: centered hover
 * controls, with a large hex code and the nearest color name pinned near the
 * bottom. Auto-selects black/white text for contrast. Supports an in-column
 * "view shades" ramp picker and per-color lock. On small screens the shade
 * ramp and the color-wheel picker open as fullscreen overlays so they are
 * comfortable to use with touch.
 */
function GeneratorColumn({
  color,
  name,
  count,
  dragHandle: DragHandle,
  onCopy,
  onToggleLock,
  onRemove,
  onChangeColor,
}) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [shadesOpen, setShadesOpen] = useState(false);
  const textColor = getBestTextColor(color.hex);
  const hexNoHash = color.hex.replace('#', '');

  const handleColumnClick = e => {
    if (e.target === e.currentTarget || e.target.dataset.hexlabel) {
      onCopy(color.hex);
    }
  };

  const stop = e => e.stopPropagation();

  const pickShade = hex => {
    onChangeColor(hex);
    setShadesOpen(false);
  };

  // Shade ramp data, computed only while the ramp is open.
  const shadeData = () => {
    const shadeColors = buildShades(color.hex);
    let baseIndex = 0;
    let best = Infinity;
    shadeColors.forEach((s, i) => {
      let d;
      try {
        d = chroma.distance(s, color.hex);
      } catch (e) {
        d = Infinity;
      }
      if (d < best) {
        best = d;
        baseIndex = i;
      }
    });
    return { shadeColors, baseIndex };
  };

  // Desktop: in-column shade ramp overlay.
  const renderInlineShades = () => {
    const { shadeColors, baseIndex } = shadeData();
    return (
      <div className={classes.shades} onClick={stop}>
        <Tooltip title="Close shades" placement="left" arrow>
          <IconButton
            size="small"
            className={classes.shadesClose}
            onClick={() => setShadesOpen(false)}
            aria-label="Close shades"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {shadeColors.map((shade, i) => (
          <div
            key={`${shade}-${i}`}
            className={classes.shadeRow}
            style={{ backgroundColor: shade, color: getBestTextColor(shade) }}
            onClick={() => pickShade(shade)}
            role="button"
            aria-label={`Use shade ${shade.replace('#', '').toUpperCase()}`}
          >
            <span className={classes.shadeLabel}>{shade.replace('#', '').toUpperCase()}</span>
          </div>
        ))}
        <div
          className={classes.shadeDot}
          style={{ top: `${((baseIndex + 0.5) / shadeColors.length) * 100}%` }}
        />
      </div>
    );
  };

  // Mobile: fullscreen shade ramp with always-visible labels and a close header.
  const renderFullscreenShades = () => {
    const { shadeColors, baseIndex } = shadeData();
    return ReactDOM.createPortal(
      <div className={classes.fsOverlay} role="dialog" aria-label="Select a shade">
        <div className={classes.fsHeader}>
          <span className={classes.fsTitle}>Select a shade</span>
          <IconButton
            className={classes.fsClose}
            onClick={() => setShadesOpen(false)}
            aria-label="Close shades"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.fsShades}>
          {shadeColors.map((shade, i) => (
            <div
              key={`${shade}-${i}`}
              className={`${classes.fsShadeRow} ${
                i === baseIndex ? classes.fsShadeRowActive : ''
              }`}
              style={{ backgroundColor: shade, color: getBestTextColor(shade) }}
              onClick={() => pickShade(shade)}
              role="button"
              aria-label={`Use shade ${shade.replace('#', '').toUpperCase()}`}
            >
              {shade.replace('#', '').toUpperCase()}
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  // Mobile: fullscreen, enlarged color-wheel picker centered on screen.
  const renderFullscreenPicker = () => {
    const pickerWidth = Math.min(
      360,
      typeof window !== 'undefined' ? window.innerWidth - 48 : 320
    );
    return ReactDOM.createPortal(
      <div className={classes.fsOverlay} role="dialog" aria-label="Adjust color">
        <div className={classes.fsHeader}>
          <span className={classes.fsTitle}>Adjust color</span>
          <IconButton
            className={classes.fsClose}
            onClick={() => setEditing(false)}
            aria-label="Close color picker"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.fsPickerBody}>
          <ChromePicker
            color={color.hex}
            disableAlpha
            width={pickerWidth}
            onChange={c => onChangeColor(c.hex)}
          />
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div
      className={classes.column}
      style={{ backgroundColor: color.hex, color: textColor }}
      onClick={handleColumnClick}
    >
      <div className={classes.toolbar} style={{ color: textColor }} onClick={stop}>
        {count > 2 && (
          <Tooltip title="Remove" placement="top" arrow>
            <IconButton
              className={classes.iconButton}
              style={{ color: textColor }}
              onClick={onRemove}
              aria-label="Remove color"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {DragHandle && <DragHandle textColor={textColor} />}
        <Tooltip title="View shades" placement="top" arrow>
          <IconButton
            className={classes.iconButton}
            style={{ color: textColor }}
            onClick={() => {
              setShadesOpen(v => !v);
              setEditing(false);
            }}
            aria-label="View shades"
          >
            <GradientIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Adjust color" placement="top" arrow>
          <IconButton
            className={classes.iconButton}
            style={{ color: textColor }}
            onClick={() => {
              setEditing(v => !v);
              setShadesOpen(false);
            }}
            aria-label="Adjust color"
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy hex" placement="top" arrow>
          <IconButton
            className={classes.iconButton}
            style={{ color: textColor }}
            onClick={() => onCopy(color.hex)}
            aria-label="Copy hex code"
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={color.locked ? 'Unlock' : 'Lock'} placement="top" arrow>
          <IconButton
            className={classes.iconButton}
            style={{ color: textColor }}
            onClick={onToggleLock}
            aria-label={color.locked ? 'Unlock color' : 'Lock color'}
          >
            {color.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Persistent lock indicator (visible without hover, like Coolors). */}
      {color.locked && (
        <LockIcon className={classes.lockBadge} fontSize="small" style={{ color: textColor }} />
      )}

      <div className={classes.info} style={{ color: textColor }}>
        <span className={classes.hex} data-hexlabel="true">
          {hexNoHash}
        </span>
        {name && <span className={classes.name}>{name}</span>}
      </div>

      {/* Color picker: inline on desktop, fullscreen overlay on mobile. */}
      {editing &&
        (isMobile ? (
          renderFullscreenPicker()
        ) : (
          <div className={classes.pickerWrap} onClick={stop}>
            <ChromePicker color={color.hex} disableAlpha onChange={c => onChangeColor(c.hex)} />
          </div>
        ))}

      {/* Shade ramp: inline on desktop, fullscreen overlay on mobile. */}
      {shadesOpen && (isMobile ? renderFullscreenShades() : renderInlineShades())}
    </div>
  );
}

export default GeneratorColumn;
